import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { MapPin, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function BookingConfirmation() {
  const { confirmationCode } = useParams();
  const navigate = useNavigate();
  const [reservation, setReservation] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReservation();
  }, [confirmationCode]);

  const fetchReservation = async () => {
    try {
      // Fetch reservation by confirmation code
      const response = await axios.get(`${API_URL}/reservations/code/${confirmationCode}`);
      setReservation(response.data);
    } catch (error) {
      console.error('Failed to fetch reservation:', error);
      toast.error('Failed to load reservation details');
      // Fallback: show basic info from URL
      setReservation({
        confirmation_code: confirmationCode,
        guest_name: 'Guest',
        inventory_label: 'N/A',
        reservation_date: new Date().toISOString(),
        status: 'confirmed',
        amount: 0
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAF9]">
        <div className="text-center">
          <div className="inline-block w-16 h-16 border-4 border-[#9F1239] border-t-transparent rounded-full animate-spin"></div>
          <p className="mt-4 text-lg font-serif text-[#9F1239]">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF9] py-12 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6">
            <CheckCircle className="w-12 h-12 text-green-600" />
          </div>
          <h1 className="text-5xl font-serif tracking-tight text-[#1C1917] mb-4" data-testid="confirmation-title">
            {reservation?.status === 'pending_authorization' ? 'Reservation Received!' : 'Reservation Confirmed!'}
          </h1>
          <p className="text-lg text-[#78716C]">
            Your booking has been successfully confirmed. Check your email for details.
          </p>
        </div>

        <div className="bg-white border border-[#E7E5E4] shadow-sm rounded-sm p-8 mb-8">
          <div className="space-y-4">
            <div className="flex justify-between border-b border-[#E7E5E4] pb-4">
              <span className="text-[#78716C]">Confirmation Code</span>
              <span className="font-serif font-bold text-[#9F1239] text-xl">{reservation?.confirmation_code}</span>
            </div>
            <div className="flex justify-between border-b border-[#E7E5E4] pb-4">
              <span className="text-[#78716C]">Guest Name</span>
              <span className="font-medium">{reservation?.guest_name}</span>
            </div>
            {reservation?.guest_email && (
              <div className="flex justify-between border-b border-[#E7E5E4] pb-4">
                <span className="text-[#78716C]">Email</span>
                <span className="font-medium">{reservation?.guest_email}</span>
              </div>
            )}
            {reservation?.guest_phone && (
              <div className="flex justify-between border-b border-[#E7E5E4] pb-4">
                <span className="text-[#78716C]">Phone</span>
                <span className="font-medium">{reservation?.guest_phone}</span>
              </div>
            )}
            <div className="flex justify-between border-b border-[#E7E5E4] pb-4">
              <span className="text-[#78716C]">Seat</span>
              <span className="font-medium">{reservation?.inventory_label}</span>
            </div>
            {reservation?.room_number && (
              <div className="flex justify-between border-b border-[#E7E5E4] pb-4">
                <span className="text-[#78716C]">{reservation.guest_type === 'resident' ? 'Unit' : 'Room'}</span>
                <span className="font-medium" data-testid="confirmation-room-number">{reservation.room_number}</span>
              </div>
            )}
            <div className="flex justify-between border-b border-[#E7E5E4] pb-4">
              <span className="text-[#78716C]">Date</span>
              <span className="font-medium">{reservation?.reservation_date ? new Date(reservation.reservation_date.slice(0, 10) + 'T12:00:00').toLocaleDateString() : ''}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-[#78716C]">Amount</span>
              <span className="font-serif font-bold text-[#9F1239] text-xl">
                {reservation?.amount > 0 ? `$${Number(reservation.amount).toFixed(2)}` : 'FREE'}
              </span>
            </div>
          </div>
        </div>

        <div className="text-center">
          <Button
            onClick={() => navigate('/')}
            className="bg-[#9F1239] hover:bg-[#881337] text-white rounded-none px-12 py-6 text-lg font-serif tracking-wide"
            data-testid="btn-back-home"
          >
            Back to Home
          </Button>
        </div>
      </div>
    </div>
  );
}
